import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Container, Grid, Alert, Typography } from '@mui/material';
import ProductCard from './ProductCard';
import { useLocalData } from '../../hooks/useLocalData';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';

/**
 * Componente container para exibição de produtos com modal de detalhes.
 * Carrega e exibe produtos da API com layout responsivo otimizado.
 * Mobile: 1 card por linha, Tablet: 2 cards por linha, Desktop: 4 cards por linha.
 * Suporta exibição de favoritos (que podem aparecer mesmo com categorias selecionadas),
 * busca de produtos e modal com detalhes completos.
 * Clique em qualquer ProductCard abre modal com informações detalhadas.
 *
 * @component
 * @returns {JSX.Element} Container responsivo com grid de produtos e modais
 */
export default function Product() {
  const { showFavorites, selectedCategory, searchQuery } = useSelector(state => state.products);
  const favorites = useSelector(state => state.favorites.items || []);
  const { products, getProductsByCategory } = useLocalData();
  const { searchAllProducts } = useGlobalSearch();

  const displayProducts = useMemo(() => {
    if (showFavorites) {
      // map: converte favoritos para formato de produtos usando dados locais
      return favorites.map(fav => {
        // Busca dados completos do produto nos dados locais
        const fullProduct = products.find(p => p.id == fav.productId);
        return fullProduct || {
          id: fav.productId,
          nome: fav.name,
          marca: fav.brand,
          preco: 0,
          imagem: fav.image,
          categoria: fav.category,
          descricao: fav.descricao || ''
        };
      });
    }
    if (selectedCategory) {
      return getProductsByCategory(selectedCategory.name);
    }
    if (searchQuery.trim() !== '') {
      // Usa pesquisa global que considera TODOS os produtos disponíveis
      return searchAllProducts(searchQuery);
    }
    return products;
  }, [showFavorites, favorites, selectedCategory, searchQuery, products, getProductsByCategory, searchAllProducts]);

  const maxLength = useMemo(
    () => (displayProducts.length === 1 ? { xs: '100%', sm: '80%', md: '50%', lg: '25%' } : '100%'),
    [displayProducts.length]
  );

  // Dados são carregados via contexto global pelo AppInitializer

  return (
    <Box component='main' sx={{ pt: { xs: 8, md: 30 }, pb: 6, minHeight: '100vh' }}>
      <Container
        maxWidth='lg'
        sx={{
          py: 3,
          px: { xs: 1, sm: 3, md: 3 },
        }}
      >
        <Typography
          variant='h5'
          component='h5'
          sx={{
            mt: { xs: 8, sm: 8, md: 0 },
            textAlign: 'left',
            fontWeight: 'bold',
          }}
        >
          {showFavorites 
            ? 'Favoritos' 
            : selectedCategory 
            ? `Produtos - ${selectedCategory.name}` 
            : searchQuery.trim() !== '' 
            ? `Resultados para "${searchQuery}"`
            : 'Produtos'
          }
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Grid
            container
            spacing={{ xs: 1, sm: 3, md: 3 }}
            sx={{
              maxWidth: { xs: '100%', sm: 1000, md: 1200 },
              width: '100%',
              margin: 0,
              mt: { xs: 1, md: 3 },
            }}
          >
            {displayProducts.length > 0 ? (
              displayProducts.map(product => (
                <Grid
                  key={product.id}
                  size={{ xs: 6, sm: 6, md: 3, lg: 3 }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    minWidth: 0,
                    maxWidth: { maxLength },
                  }}
                >
                  <ProductCard
                    id={product.id}
                    nome={product.nome}
                    marca={product.marca}
                    preco={product.preco}
                    imagem={product.imagem}
                    categoria={product.categoria}
                    descricao={product.descricao}
                  />
                </Grid>
              ))
            ) : (
              <Grid size={12}>
                <Alert severity='info'>
                  {showFavorites 
                    ? 'Nenhum produto favoritado ainda'
                    : selectedCategory 
                    ? `Nenhum produto encontrado na categoria '${selectedCategory.name}'`
                    : searchQuery.trim() !== ''
                    ? `Nenhum produto encontrado para "${searchQuery}"`
                    : 'Nenhum produto encontrado'
                  }
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
}
